import { FormEvent, useState } from "react";
import { createSlotsBatch } from "../../../lib/adminApi";
import { useToast } from "../../../components/Toast";
import { ymd, generateTimeSlots } from "../../../lib/dates";

type Props = {
  initialWeekStart: Date;
  onClose: () => void;
  onCreated: () => void;
};

const DAYS = [
  { value: 1, label: "Po" },
  { value: 2, label: "Út" },
  { value: 3, label: "St" },
  { value: 4, label: "Čt" },
  { value: 5, label: "Pá" },
  { value: 6, label: "So" },
  { value: 0, label: "Ne" },
];

/** Všechny "HH:MM" v půlhodinových krocích od 00:00 do 23:30 + 24:00 pro konec */
const START_OPTIONS = generateTimeSlots(0, 24 * 60);
const END_OPTIONS = generateTimeSlots(30, 24 * 60 + 30);

export default function BatchCreateModal({ initialWeekStart, onClose, onCreated }: Props) {
  const toast = useToast();
  const end = new Date(initialWeekStart);
  end.setUTCDate(end.getUTCDate() + 6);

  const [dateFrom, setDateFrom] = useState(ymd(initialWeekStart));
  const [dateTo, setDateTo] = useState(ymd(end));
  const [timeFrom, setTimeFrom] = useState("09:00");
  const [timeTo, setTimeTo] = useState("16:00");
  const [daysOfWeek, setDaysOfWeek] = useState<number[]>([1, 2, 3, 4, 5]); // Po–Pá
  const [saving, setSaving] = useState(false);

  function toggleDay(d: number) {
    setDaysOfWeek((xs) => (xs.includes(d) ? xs.filter((x) => x !== d) : [...xs, d]));
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (daysOfWeek.length === 0) {
      toast.show("error", "Vyber aspoň jeden den v týdnu.");
      return;
    }
    setSaving(true);
    try {
      const res = await createSlotsBatch({ dateFrom, dateTo, timeFrom, timeTo, daysOfWeek });
      toast.show(
        "success",
        `Vytvořeno ${res.created} slotů${res.skipped > 0 ? ` (${res.skipped} už existovalo)` : ""}.`
      );
      onCreated();
      onClose();
    } catch (err: any) {
      toast.show("error", err.message ?? "Vytvoření selhalo.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <form
        onSubmit={onSubmit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl"
      >
        <h2 className="text-xl font-bold text-slate-900">Vygenerovat sloty</h2>
        <p className="mt-1 text-sm text-slate-600">
          Vytvoří 30-min sloty v zadaném rozsahu dnů a časů. Existující sloty se nepřepíší.
        </p>

        <div className="mt-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-slate-700">Od data</span>
              <input
                type="date"
                className="input"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                required
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-slate-700">Do data</span>
              <input
                type="date"
                className="input"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                required
              />
            </label>
          </div>

          <div>
            <span className="mb-2 block text-sm font-medium text-slate-700">Dny v týdnu</span>
            <div className="flex flex-wrap gap-2">
              {DAYS.map((d) => (
                <button
                  key={d.value}
                  type="button"
                  onClick={() => toggleDay(d.value)}
                  className={`rounded-lg border px-3 py-1.5 text-sm font-medium transition ${
                    daysOfWeek.includes(d.value)
                      ? "border-obloha-indigo-600 bg-obloha-indigo-50 text-obloha-indigo-800"
                      : "border-slate-300 bg-white text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  {d.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-slate-700">Od hodiny</span>
              <select
                className="input"
                value={timeFrom}
                onChange={(e) => setTimeFrom(e.target.value)}
              >
                {START_OPTIONS.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-slate-700">Do hodiny</span>
              <select
                className="input"
                value={timeTo}
                onChange={(e) => setTimeTo(e.target.value)}
              >
                {END_OPTIONS.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="rounded-lg bg-slate-50 p-3 text-xs text-slate-600">
            Poslední slot bude <strong>{backTime(timeTo)}–{timeTo}</strong>. Sloty jsou 30-min bloky.
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button type="button" onClick={onClose} className="btn-secondary">
            Zrušit
          </button>
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? "Vytvářím…" : "Vytvořit sloty"}
          </button>
        </div>
      </form>
    </div>
  );
}

/** "16:00" → "15:30" (poslední slot začíná 30 min před koncem) */
function backTime(hhmm: string): string {
  const [h, m] = hhmm.split(":").map(Number);
  const total = h * 60 + m - 30;
  return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
}
